import { useRef, useEffect } from 'react';
import { SPRITES_BY_ID, EYES_BY_ID, MOUTHS_BY_ID } from '../features/avatar/avatarData';
import type { PaletteColors, SpriteGrid } from '../features/avatar/avatarTypes';

interface MiniAvatarCanvasProps {
  config: Record<string, unknown>;
  size?: number;
}

function indexToColor(index: number, palette: PaletteColors): string | null {
  switch (index) {
    case 0: return null;
    case 1: return palette.bodyPrimary;
    case 2: return palette.bodySecondary;
    case 3: return palette.eyeColor;
    case 4: return palette.mouthColor;
    case 5: return palette.accessoryColor;
    default: return null;
  }
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  grid: SpriteGrid,
  palette: PaletteColors,
  scale: number,
  offsetX: number,
  offsetY: number,
) {
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const idx = grid[row][col];
      const color = indexToColor(idx, palette);
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(offsetX + col * scale, offsetY + row * scale, scale, scale);
    }
  }
}

/**
 * A tiny static canvas rendering of a custom pixel avatar,
 * used in the header dropdown and anywhere a small avatar preview is needed.
 */
export default function MiniAvatarCanvas({ config, size = 28 }: MiniAvatarCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const entityType = config.entityType as string;
    const palette = config.palette as PaletteColors;
    const gridSize = (config.gridSize as number) || 14;
    const scale = size / gridSize;

    canvas.width = size;
    canvas.height = size;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, size, size);

    // Background
    ctx.fillStyle = palette?.backgroundColor || '#1e1e2e';
    ctx.fillRect(0, 0, size, size);

    // Base sprite
    const sprite = SPRITES_BY_ID[entityType];
    if (!sprite || !palette) return;
    drawGrid(ctx, sprite.grid, palette, scale, 0, 0);

    // Eyes
    const eyes = config.eyes as string;
    const eyeDef = EYES_BY_ID[eyes];
    if (eyeDef) {
      const [ar, ac] = sprite.anchorPoints.eyes;
      drawGrid(ctx, eyeDef.grid, palette, scale, ac * scale, ar * scale);
    }

    // Mouth
    const mouth = config.mouth as string;
    const mouthDef = MOUTHS_BY_ID[mouth];
    if (mouthDef) {
      const [ar, ac] = sprite.anchorPoints.mouth;
      drawGrid(ctx, mouthDef.grid, palette, scale, ac * scale, ar * scale);
    }
  }, [config, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        imageRendering: 'pixelated',
      }}
    />
  );
}
